// Console: قاب مشترک ← PlatformGate (فقط کاربر پلتفرم، بدون انتخاب مستأجر) ← Router.
import { AppFrame, ForbiddenState, PlatformGate, type AuthClient } from '@phoenix/auth';
import type { QueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  createBrowserRouter,
  createMemoryRouter,
  Outlet,
  RouterProvider,
  type RouteObject,
} from 'react-router';
import { usePlatformAccess } from './access';
import { ConsoleLayout } from './ConsoleLayout';
import { consoleMenu } from './navigation';
import { AnnouncementsPage } from './pages/announcements/AnnouncementsPage';
import { AuditPage } from './pages/audit/AuditPage';
import {
  ConsoleComingSoonPage,
  ConsoleHomePage,
  ConsoleNotFoundPage,
  ConsoleRouteErrorPage,
} from './pages/ConsolePages';
import { CompliancePage } from './pages/integrations/CompliancePage';
import { TaminRegistrationPage } from './pages/integrations/TaminRegistrationPage';
import { LicensesPage } from './pages/licenses/LicensesPage';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage';
import { SpecialtyPacksPage } from './pages/packs/SpecialtyPacksPage';
import { PlansPage } from './pages/plans/PlansPage';
import { ReferenceDataPage, RegulatoryListsPage } from './pages/reference-data/ReferenceDataPage';
import { SupportAccessPage } from './pages/support/SupportAccessPage';
import { SupportTicketsPage } from './pages/support/SupportTicketsPage';
import { TenantCreatePage } from './pages/tenants/TenantCreatePage';
import { TenantDetailPage } from './pages/tenants/TenantDetailPage';
import { TenantListPage } from './pages/tenants/TenantListPage';

/** محافظ Route بر پایه Permission پلتفرم (نگاشت نقش — C-AGENT-200)؛ تصمیم نهایی با سرور. */
export function RequirePlatformPermission({
  permission,
  children,
}: {
  permission?: string;
  children: ReactNode;
}) {
  const access = usePlatformAccess();
  if (permission && !access.can(permission)) {
    return <ForbiddenState />;
  }
  return <>{children}</>;
}

const HOME_CRUMB = { labelKey: 'console:menu.home', path: '/' };
const TENANTS_CRUMB = { labelKey: 'console:menu.tenants', path: '/tenants' };

const IMPLEMENTED: Record<string, ReactNode> = {
  tenants: <TenantListPage />,
  plans: <PlansPage />,
  'reference-data': <ReferenceDataPage />,
  // AP-DOC-03 (US-CFG-042؛ SCR-CONSOLE-041).
  'regulatory-lists': <RegulatoryListsPage />,
  // AP-PLT-08.
  licenses: <LicensesPage />,
  audit: <AuditPage />,
  integrations: <IntegrationsPage />,
  compliance: <CompliancePage />,
  // AP-INT-10: ثبت نرم‌افزار نزد تامین و کاتالوگ نگاشت خطا.
  'tamin-registration': <TaminRegistrationPage />,
  // AP-PLT-05.
  'support-access': <SupportAccessPage />,
  announcements: <AnnouncementsPage />,
  'support-tickets': <SupportTicketsPage />,
  // AP-CFG-01.
  'specialty-packs': <SpecialtyPacksPage />,
};

export function createConsoleRoutes(options: {
  apiBaseUrl: string;
  fetch?: typeof globalThis.fetch;
}): RouteObject[] {
  return [
    {
      element: (
        <PlatformGate baseUrl={options.apiBaseUrl} fetch={options.fetch}>
          <Outlet />
        </PlatformGate>
      ),
      errorElement: <ConsoleRouteErrorPage />,
      children: [
        {
          element: <ConsoleLayout />,
          children: [
            { index: true, element: <ConsoleHomePage /> },
            ...consoleMenu
              .filter((item) => item.path !== '/')
              .map((item) => ({
                path: item.path.slice(1),
                handle: { crumbs: [HOME_CRUMB, { labelKey: item.labelKey }] },
                element: (
                  <RequirePlatformPermission permission={item.platformPermission}>
                    {(item.implemented && IMPLEMENTED[item.id]) || (
                      <ConsoleComingSoonPage screenId={item.screenId ?? item.id} titleKey={item.labelKey} />
                    )}
                  </RequirePlatformPermission>
                ),
              })),
            {
              path: 'tenants/new',
              handle: { crumbs: [HOME_CRUMB, TENANTS_CRUMB, { labelKey: 'console:tenants.create.title' }] },
              element: (
                <RequirePlatformPermission permission="platform.tenant.create">
                  <TenantCreatePage />
                </RequirePlatformPermission>
              ),
            },
            {
              path: 'tenants/:id',
              handle: { crumbs: [HOME_CRUMB, TENANTS_CRUMB, { labelKey: 'console:tenant.crumb' }] },
              element: (
                <RequirePlatformPermission permission="platform.tenant.view">
                  <TenantDetailPage />
                </RequirePlatformPermission>
              ),
            },
            { path: '*', element: <ConsoleNotFoundPage /> },
          ],
        },
      ],
    },
  ];
}

export interface ConsoleAppProps {
  authClient: AuthClient;
  apiBaseUrl: string;
  memoryEntries?: string[];
  queryClient?: QueryClient;
}

export function ConsoleApp({ authClient, apiBaseUrl, memoryEntries, queryClient }: ConsoleAppProps) {
  const [router] = useState(() => {
    const routes = createConsoleRoutes({ apiBaseUrl });
    return memoryEntries
      ? createMemoryRouter(routes, { initialEntries: memoryEntries })
      : createBrowserRouter(routes);
  });
  return (
    <AppFrame authClient={authClient} queryClient={queryClient}>
      <RouterProvider router={router} />
    </AppFrame>
  );
}
